import { memo } from "react";
import { MiAutocomplete, MiButton } from "@miplatform/components";
import { CONTACT_CARD_EDIT_MODES } from "@miplatform/constants";
import { AccountType, IContactDetailsResponse } from "@miplatform/types";
import { mdiPlus } from "@mdi/js";
import { MiContactCard } from "@mibuildweb/components";
import { Box, MenuItem, Paper, Typography } from "@mui/material";

interface RequesterFieldProps {
  value?: string;
  contacts: IContactDetailsResponse[];
  builderId?: string;
  error?: { message: string };
  required?: boolean;
  isCreatingContact: boolean;
  setIsCreatingContact: (isCreating: boolean) => void;
  onChange: (contactId: string) => void;
  onContactCreated: (contact: IContactDetailsResponse) => void;
}

export function RequesterField({
  value,
  contacts,
  builderId,
  error,
  required,
  isCreatingContact,
  setIsCreatingContact,
  onChange,
  onContactCreated,
}: Readonly<RequesterFieldProps>) {
  const handleCreateContact = (contact: IContactDetailsResponse) => {
    setIsCreatingContact(false);
    onContactCreated(contact);
  };

  if (isCreatingContact) {
    return (
      <MiContactCard
        elevation={0}
        editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
        accountTypes={[AccountType.Builder]}
        defaultAccountId={builderId}
        onRefresh={handleCreateContact}
        onCloseEdit={() => setIsCreatingContact(false)}
      />
    );
  }

  const options = contacts.map((contact) => ({
    value: contact.id,
    label: `${contact.firstName} ${contact.lastName}`,
  }));

  return (
    <MiAutocomplete
      name="requesterId"
      label="Requester"
      required={required}
      error={error}
      options={options}
      isOptionEqualToValue={(
        option: { value?: string | number },
        val: { value?: string | number } | string
      ) => option.value === (typeof val === "string" ? val : val?.value)}
      value={options.find((option) => option.value === value) ?? null}
      onChange={(
        _e: React.SyntheticEvent,
        newValue: { value?: string | number } | null
      ) => onChange((newValue?.value as string) ?? "")}
      renderOption={(
        { ...props }: React.HTMLAttributes<HTMLLIElement>,
        option: { label: string }
      ) => (
        <MenuItem key={option.label} {...props}>
          <Typography>{option.label}</Typography>
        </MenuItem>
      )}
      slotProps={{
        paper: {
          onMouseDown: () => setIsCreatingContact(true),
        },
      }}
      PaperComponent={CreateContactPaper}
    />
  );
}

const CreateContactPaper = memo(({ children, onMouseDown, ...props }: any) => (
  <Paper {...props}>
    <Box display="flex" flexDirection="column" minWidth="fit-content">
      <MiButton
        fullWidth
        title="Create New Contact"
        startIcon={{ path: mdiPlus }}
        sx={{
          borderRadius: 0,
          justifyContent: "flex-start",
          color: "blue.main",
          pl: 4,
          mb: -2,
        }}
        onMouseDown={onMouseDown}
      />
      {children}
    </Box>
  </Paper>
));
